import { COUNTRIES, type PlateStyle } from "@/constants/countries";
import { useThemeColor } from "@/hooks/useThemeColor";
import React from "react";
import { StyleSheet, TouchableOpacity } from "react-native";
import { ThemedText } from "../ThemedText";
import { ThemedView } from "../ThemedView";

const getPlateStyles = (country: string, plate: string): PlateStyle | null => {
  const countryData = COUNTRIES.find((c) => c.value === country);
  const regexes = countryData?.vehiclePlateRegexes;
  const styles = countryData?.vehiclePlateStyles;
  if (!regexes || !styles) {
    return null;
  }
  for (const [variant, regex] of Object.entries(regexes)) {
    if (regex.test(plate)) {
      return styles[variant] ?? null;
    }
  }
  return null;
};

const VehiclePlate = ({
  country,
  plate,
  onDelete,
  style,
}: {
  country: string;
  plate: string;
  onDelete?: () => void;
  style?: object;
}) => {
  const borderColor = useThemeColor({}, "icon");
  const countryData = COUNTRIES.find((c) => c.value === country);
  const customStyles = getPlateStyles(country, plate);

  const plateContainerStyle = [
    styles.plateContainer,
    { borderColor },
    customStyles?.container,
    style,
  ];

  const plateTextStyle = [styles.plateText, customStyles?.text];

  return (
    <ThemedView style={plateContainerStyle}>
      <ThemedView style={[styles.header, customStyles?.header]}>
        {countryData && (
          <ThemedText style={styles.flagText}>{countryData.flag}</ThemedText>
        )}
        {customStyles?.countryName && (
          <ThemedText style={[styles.countryName, customStyles?.countryName]}>
            {(countryData?.label || country).toUpperCase()}
          </ThemedText>
        )}
      </ThemedView>
      <ThemedView style={[styles.plateTextContainer]}>
        {customStyles?.customPlate ? (
          customStyles.customPlate(plate)
        ) : (
          <ThemedText style={plateTextStyle}>{plate}</ThemedText>
        )}
      </ThemedView>
      {onDelete && (
        <TouchableOpacity style={styles.deleteButton} onPress={onDelete}>
          <ThemedText style={styles.deleteButtonText}>×</ThemedText>
        </TouchableOpacity>
      )}
    </ThemedView>
  );
};

const styles = StyleSheet.create({
  plateContainer: {
    flexDirection: "column",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 4,
    marginRight: 4,
    position: "relative",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    width: "100%",
  },
  countryName: {
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "center",
    flex: 1,
    color: "#333",
  },
  flagText: {
    fontSize: 15,
  },
  plateTextContainer: {
    flexDirection: "column",
    alignItems: "center",
    paddingHorizontal: 8,
  },
  countryText: {
    fontWeight: "bold",
    fontSize: 12,
    color: "#333",
  },
  plateText: {
    fontWeight: "bold",
    fontSize: 16,
    color: "#333",
  },
  deleteButton: {
    position: "absolute",
    top: 2,
    right: 2,
    backgroundColor: "#ff0000",
    borderRadius: 8,
    width: 16,
    height: 16,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
  },
  deleteButtonText: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: 10,
    lineHeight: 12,
  },
});

export default VehiclePlate;
